// ActivityHeatmap — a GitHub-contribution-style grid of per-day activity.
// Reused for the big "Runs this year" heatmap in the Activity Overview and for
// the compact per-agent heatmaps in the By-agent view.
//
// `counts` is a plain map keyed by local ISO day ('YYYY-MM-DD') → number. Callers
// build it from server buckets (already 'YYYY-MM-DD') or by bucketing runs with
// `isoDay()` below, so grid cells and counts share the exact same key shape and
// no timezone conversion drifts a run into the wrong cell.
import { useMemo } from 'react';

const DAY_MS = 86400000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Local ISO day key. Use for both the grid and for bucketing runs, so keys match.
export const isoDay = (d) => {
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return null;
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const levelForCount = (n) => {
  if (!n) return 0;
  if (n <= 1) return 1;
  if (n <= 3) return 2;
  if (n <= 6) return 3;
  return 4;
};

const LEVEL_BG = {
  0: 'bg-white/[0.04]',
  1: 'bg-violet-500/25',
  2: 'bg-violet-500/45',
  3: 'bg-violet-500/70',
  4: 'bg-violet-400',
};

const CELL = {
  md: 'w-[11px] h-[11px]',
  sm: 'w-[9px] h-[9px]',
  // Móvil. 53 columnas a 11px + 3px de aire se van a 742px: cinco veces el
  // ancho útil de un teléfono, y lo que se ve al abrir es el trozo de octubre.
  // A 5px el año entra casi entero y el resto lo resuelve el scroll que la
  // tarjeta ya tenía; un año que se lee de un vistazo es justo el punto de un
  // heatmap.
  xs: 'w-[5px] h-[5px]',
};

// El aire entre celdas acompaña al tamaño: a 5px, 3px de separación pesan más
// que el propio dato y la mancha deja de leerse como una mancha.
const GAP = {
  md: 'gap-[3px]',
  sm: 'gap-[3px]',
  xs: 'gap-[2px]',
};

const LegendCell = ({ level, size }) => (
  <span className={`${CELL[size] || CELL.md} rounded-[2px] ${LEVEL_BG[level]}`} />
);

/**
 * @param {Object} props
 * @param {Object<string,number>} props.counts - { 'YYYY-MM-DD': count }
 * @param {number} [props.weeks=53] - columns of history (7 days each)
 * @param {'xs'|'sm'|'md'} [props.size='md']
 * @param {boolean} [props.months=true] - show month labels above the grid
 * @param {boolean} [props.legend=false] - show the Less…More legend below
 */
export default function ActivityHeatmap({
  counts = {},
  weeks = 53,
  size = 'md',
  months = true,
  legend = false,
}) {
  const { cols, monthLabels } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today.getTime() - weeks * 7 * DAY_MS);
    start.setDate(start.getDate() - start.getDay()); // back to Sunday

    const grid = [];
    const labels = [];
    let cursor = new Date(start);
    let lastMonth = -1;

    while (cursor <= today) {
      const col = [];
      for (let d = 0; d < 7; d += 1) {
        const cell = new Date(cursor);
        const future = cell > today;
        const key = isoDay(cell);
        const count = future ? 0 : counts[key] || 0;
        col.push({ key, count, level: future ? -1 : levelForCount(count), date: cell });
        cursor = new Date(cursor.getTime() + DAY_MS);
      }
      const firstMonth = col[0].date.getMonth();
      if (firstMonth !== lastMonth) {
        labels.push({ col: grid.length, label: MONTHS[firstMonth] });
        lastMonth = firstMonth;
      }
      grid.push(col);
    }
    return { cols: grid, monthLabels: labels };
  }, [counts, weeks]);

  const cellCls = CELL[size] || CELL.md;
  const gapCls = GAP[size] || GAP.md;
  const labelByCol = useMemo(
    () => new Map(monthLabels.map((m) => [m.col, m.label])),
    [monthLabels]
  );

  return (
    <div className="inline-flex flex-col gap-1.5">
      <div className="overflow-x-auto">
        {months && (
          // Mismo flex, mismos anchos y mismo gap que las celdas. Cuando esto
          // era un grid de fracciones iguales, la fila de etiquetas se estiraba
          // al ancho del contenedor mientras las celdas medían 11px fijos: las
          // 53 columnas ocupaban 742px y las etiquetas mil, así que agosto
          // quedaba escrito sobre mayo. La actividad estaba bien puesta; el
          // rótulo de arriba, no.
          <div className={`flex ${gapCls} mb-1 h-3`} aria-hidden="true" data-testid="heatmap-months">
            {cols.map((col, ci) => (
              <span key={ci} className={`${cellCls} relative`}>
                {labelByCol.get(ci) && (
                  <span className="absolute left-0 top-0 text-[10px] leading-none text-gray-500 whitespace-nowrap">
                    {labelByCol.get(ci)}
                  </span>
                )}
              </span>
            ))}
          </div>
        )}
        <div className={`flex ${gapCls}`} data-testid="heatmap-grid">
          {cols.map((col, ci) => (
            <div key={ci} className={`flex flex-col ${gapCls}`}>
              {col.map((cell) => (
                <span
                  key={cell.key}
                  className={`${cellCls} rounded-[2px] ${cell.level < 0 ? 'bg-transparent' : LEVEL_BG[cell.level]}`}
                  title={
                    cell.level >= 0
                      ? `${cell.count} run${cell.count === 1 ? '' : 's'} · ${cell.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                      : undefined
                  }
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {legend && (
        <div className="flex items-center gap-1.5 text-[10px] text-gray-500 self-end">
          <span>Less</span>
          <LegendCell level={0} size={size} />
          <LegendCell level={1} size={size} />
          <LegendCell level={2} size={size} />
          <LegendCell level={3} size={size} />
          <LegendCell level={4} size={size} />
          <span>More</span>
        </div>
      )}
    </div>
  );
}
